import { useColorScheme } from "@mui/material/styles";
import { createContext, type ReactNode, useContext } from "react";

type ThemeMode = "light" | "dark";

interface ThemeModeContextValue {
  mode: ThemeMode;
  toggleMode: () => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | undefined>(undefined);

export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const { mode, setMode } = useColorScheme();

  // Ensure we always expose a concrete ThemeMode ("light" or "dark"),
  // even if `mode` is "system" or undefined during initial render.
  const resolvedMode: ThemeMode = mode === "dark" ? "dark" : "light";

  const toggleMode = () => {
    setMode(resolvedMode === "light" ? "dark" : "light");
  };

  return (
    <ThemeModeContext.Provider value={{ mode: resolvedMode, toggleMode }}>
      {children}
    </ThemeModeContext.Provider>
  );
}

export function useThemeMode() {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error("useThemeMode must be used within ThemeModeProvider");
  }
  return context;
}
